import { MutableRefObject, useEffect, useRef } from 'react';

type UseDwellInViewOptions = {
  onDwell: () => void;
  enabled?: boolean;
  threshold?: number; // 0..1, fraction of element visible required
  dwellMs?: number; // milliseconds to remain in view before firing
  once?: boolean; // if true, fire only once
  root?: Element | null;
  rootMargin?: string;
  tolerance?: number; // allow small tolerance for fractional pixel issues
  requireScroll?: boolean; // if true, only track after user has scrolled
};

export function useDwellInView(
  targetRef: MutableRefObject<Element | null>,
  {
    onDwell,
    enabled = true,
    threshold = 1,
    dwellMs = 3000,
    once = true,
    root = null,
    rootMargin,
    tolerance = 0.001,
    requireScroll = false,
  }: UseDwellInViewOptions
) {
  const timerRef = useRef<number | undefined>(undefined);
  const hasFiredRef = useRef(false);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    const element = targetRef.current;
    if (!enabled || !element) return;
    if (once && hasFiredRef.current) return;

    // Set up scroll listener if required
    const handleScroll = () => {
      hasScrolledRef.current = true;
    };

    if (requireScroll) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    const handle = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (once && hasFiredRef.current) return;
      if (requireScroll && !hasScrolledRef.current) return;

      const isVisibleEnough =
        entry.isIntersecting && entry.intersectionRatio + tolerance >= threshold;
      if (isVisibleEnough) {
        if (timerRef.current === undefined) {
          timerRef.current = window.setTimeout(() => {
            if (once && hasFiredRef.current) return;
            hasFiredRef.current = true;
            onDwell();
          }, dwellMs);
        }
      } else if (timerRef.current !== undefined) {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };

    const observer = new IntersectionObserver(handle, {
      threshold,
      root,
      rootMargin,
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      if (requireScroll) {
        window.removeEventListener('scroll', handleScroll);
      }
      if (timerRef.current !== undefined) {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [enabled, threshold, dwellMs, once, onDwell, root, rootMargin, requireScroll, targetRef]);
}




